import { type Static, Type } from '@sinclair/typebox';
import { decodeBase64UrlJson } from './utils';

/**
 * Standard public OAuth callback response schema.
 * Returns an acknowledgment message and an optional sanitized redirect URL.
 */
export const PublicOAuthCallbackResponseSchema = Type.Object({
  message: Type.String(),
  redirect_url: Type.Optional(Type.String()),
});

export type PublicOAuthCallbackResponseObject = Static<typeof PublicOAuthCallbackResponseSchema>;

/**
 * Payload d'un `state` OAuth signé par le backend (`<base64url(JSON)>.<base64url(HMAC)>`,
 * cf. `signHmacState`), décodé SANS vérification de signature, ou null si illisible.
 * ⚠️ Sert uniquement à choisir un écran, jamais à décider d'une action (règle `11` §1).
 */
export function decodeSignedStatePayload(state: string | null | undefined): Record<string, unknown> | null {
  const encoded = state?.split('.', 1)[0];
  if (!encoded) return null;
  try {
    const payload = decodeBase64UrlJson(encoded);
    return payload !== null && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Parcours OAuth lancé depuis l'app mobile (`from_mobile_app` du payload, cf.
 * `BaseOAuthStatePayload` côté backend). Indication d'affichage uniquement : elle choisit
 * la cible du retour (app ou web) de la page de callback, lisible même quand l'échange
 * échoue (annulation Google, state expiré).
 */
export function isMobileAppOAuthState(state: string | null | undefined): boolean {
  return decodeSignedStatePayload(state)?.from_mobile_app === true;
}
